import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Upload, FileText, LayoutDashboard, LogOut, Send, Download,
  Search, ChevronRight, User, Building2, Phone, Mail, Eye,
  MessageSquare, Clock, CheckCircle, XCircle, Loader2, AlertCircle,
  Settings, Paperclip, ChevronLeft, CreditCard, ShieldCheck, UserCog, MapPin, Save,
  Zap, Euro, Car
} from "lucide-react";
import { useSiteMeta } from "./hooks/useSiteMeta";
import { useContactInfo } from "./hooks/useContactInfo";
import { useFlexfuelOffer, resolveOffer } from "./hooks/useFlexfuelOffer";

const API = process.env.REACT_APP_BACKEND_URL + "/api/file-service";

/* ATR Color Theme Constants */
const THEME = {
  bg: "#0A0A0A",
  bgCard: "#121212",
  bgInput: "#1C1C1E",
  bgSidebar: "#0A0A0A",
  accent: "#FF3B30",
  accentHover: "#D63025",
  border: "white/[0.06]",
};

const STATUS_MAP = {
  pending: { label: "En attente", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  in_progress: { label: "En cours", color: "bg-blue-500/20 text-blue-400", icon: Loader2 },
  completed: { label: "Terminé", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  cancelled: { label: "Annulé", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

// ============== PUBLIC LANDING ==============
const FileServiceLanding = ({ onOpenAuth }) => {
  const offer = useFlexfuelOffer();
  // Default content (used as fallback if API fetch fails)
  const defaultContent = useMemo(() => ({
    hero: {
      badge: "Service Fichiers ATR",
      title_line_1: "Reprogrammation moteur",
      title_line_2: "100 % en ligne",
      subtitle: "Envoyez votre fichier ECU, recevez la version optimisée sous 24h. Gain de puissance, Flexfuel, suppressions — tout se fait depuis votre espace client.",
      cta_primary: "Commencer maintenant",
      cta_secondary: "Voir les tarifs →",
    },
    steps_badge: "Fonctionnement",
    steps_title: "En 3 étapes",
    steps: [
      { title: "Créez votre compte", desc: "Inscription rapide (particulier ou professionnel). Vérification immédiate, accès à votre espace personnel." },
      { title: "Envoyez votre fichier ECU", desc: "Téléversez le fichier d'origine extrait de votre calculateur. Indiquez-nous le type de prestation souhaitée." },
      { title: "Recevez votre fichier modifié", desc: "Paiement sécurisé par Stripe, livraison du fichier optimisé sous 24h ouvrées." },
    ],
    services_badge: "Tarifs",
    services_title: "Nos prestations",
    services_note: "Prix indicatifs — un tarif définitif vous est confirmé avant paiement.",
    services: [
      { title: "Stage 1", price: "300 € TTC", desc: "Optimisation complète du calculateur moteur pour un gain de puissance immédiat (+15 à 25 % en moyenne), sans modification mécanique." },
      { title: "Stage 2", price: "400 € TTC", desc: "Reprogrammation poussée avec prise en compte des modifications mécaniques (downpipe, admission, intercooler…)." },
      { title: "Conversion Flexfuel E85", price: "{prix_flexfuel}", desc: "Passez au bioéthanol E85 par mise à jour calculateur. Économies carburant jusqu'à 50%, opération réversible." },
      { title: "Suppression EGR / FAP / AdBlue", price: "Sur devis", desc: "Neutralisation propre des dispositifs anti-pollution (usage piste uniquement)." },
    ],
    advantages_badge: "Pourquoi ATR",
    advantages_title: "Nos engagements",
    advantages: [
      "Tarifs transparents, pas de frais cachés",
      "Livraison en 24h ouvrées",
      "Paiement sécurisé Stripe",
      "Support & messagerie intégrée",
      "Réversibilité de toutes nos prestations",
      "Équipe professionnelle certifiée",
    ],
    final_cta: {
      title: "Prêt à optimiser votre moteur ?",
      subtitle: "Créez votre compte en 30 secondes et envoyez votre premier fichier.",
      button: "Créer mon compte gratuitement",
    },
  }), []);

  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const BASE = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BASE}/api/content/fs_landing`)
      .then(r => setContent({ ...defaultContent, ...r.data }))
      .catch(() => {});
  }, [defaultContent]);

  const stepIcons = [User, Upload, Download];
  const serviceIcons = [Zap, Zap, Car, ShieldCheck];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-[#0A0A0A]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-xl font-black tracking-tighter uppercase italic">
            AUVERGNE<span className="text-[#FF3B30]"> TECH RACING</span>
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onOpenAuth("login")}
              data-testid="fs-landing-login-btn"
              className="text-white/70 hover:text-white text-sm font-medium px-4 py-2 transition-colors"
            >
              Se connecter
            </button>
            <button
              onClick={() => onOpenAuth("register")}
              data-testid="fs-landing-register-btn"
              className="bg-[#FF3B30] hover:bg-[#D63025] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors"
            >
              Créer un compte
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-5">
            {content.hero.badge}
          </p>
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter uppercase text-white leading-[0.95]">
            {content.seo?.h1_prefix || content.hero.title_line_1}<br />
            <span className="text-[#FF3B30]">{content.seo?.h1_highlight || content.hero.title_line_2}</span>
          </h1>
          <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {content.hero.subtitle}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => onOpenAuth("register")}
              data-testid="fs-landing-hero-cta"
              className="bg-[#FF3B30] hover:bg-[#D63025] text-white font-semibold px-8 py-3.5 rounded-lg text-sm transition-all shadow-[0_0_20px_rgba(255,59,48,0.3)]"
            >
              {content.hero.cta_primary}
            </button>
            <a
              href="#tarifs"
              className="text-white/70 hover:text-white text-sm px-6 py-3.5 transition-colors"
            >
              {content.hero.cta_secondary}
            </a>
          </div>
        </div>
      </section>

      {/* Process / 3 steps */}
      <section className="py-20 px-6 bg-[#0F0F0F] border-y border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3">{content.steps_badge}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">{content.steps_title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {content.steps.map((s, idx) => {
              const Icon = stepIcons[idx] || User;
              return (
                <div
                  key={idx}
                  data-testid={`fs-landing-step-${idx + 1}`}
                  className="bg-[#121212] border border-white/[0.06] rounded-xl p-6 relative"
                >
                  <span className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#FF3B30] text-white font-black text-sm flex items-center justify-center shadow-lg">
                    {idx + 1}
                  </span>
                  <Icon size={28} className="text-[#FF3B30] mb-4" />
                  <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prestations / tarifs */}
      <section id="tarifs" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3">{content.services_badge}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">{content.services_title}</h2>
            {content.services_note && (
              <p className="text-white/50 text-sm mt-3">{content.services_note}</p>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {content.services.map((s, i) => {
              const Icon = serviceIcons[i] || Zap;
              return (
                <div
                  key={i}
                  data-testid={`fs-landing-service-${i}`}
                  className="bg-[#121212] border border-white/[0.06] rounded-xl p-6 flex gap-5 hover:border-[#FF3B30]/30 transition-colors"
                >
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                    <Icon size={22} className="text-[#FF3B30]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3 mb-1.5">
                      <h3 className="font-bold text-white">{s.title}</h3>
                      <span className="text-[#FF3B30] font-bold whitespace-nowrap">{resolveOffer(s.price, offer)}</span>
                    </div>
                    <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 px-6 bg-[#0F0F0F] border-y border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-3">{content.advantages_badge}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">{content.advantages_title}</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {content.advantages.map((a, i) => (
              <div
                key={i}
                className="bg-[#121212] border border-white/[0.06] rounded-lg p-4 flex items-center gap-3"
              >
                <CheckCircle size={18} className="text-[#FF3B30] shrink-0" />
                <span className="text-sm text-white/80">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto bg-[#121212] border border-white/[0.06] rounded-2xl p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-4">
            {content.final_cta.title}
          </h2>
          <p className="text-white/60 text-sm md:text-base mb-8">
            {content.final_cta.subtitle}
          </p>
          <button
            onClick={() => onOpenAuth("register")}
            data-testid="fs-landing-final-cta"
            className="bg-[#FF3B30] hover:bg-[#D63025] text-white font-semibold px-8 py-3.5 rounded-lg text-sm transition-all shadow-[0_0_20px_rgba(255,59,48,0.3)]"
          >
            {content.final_cta.button}
          </button>
          <p className="mt-4 text-white/40 text-xs">
            Déjà client&nbsp;?{" "}
            <button
              onClick={() => onOpenAuth("login")}
              className="text-[#FF3B30] hover:text-[#ff6b62] underline underline-offset-2"
            >
              Se connecter
            </button>
          </p>
        </div>
      </section>

      {/* ============== SEO CONTENT SECTION ============== */}
      <section
        className="border-t border-white/[0.06] bg-[#0A0A0A] px-6 py-16"
        data-testid="fs-seo-content"
        aria-labelledby="fs-seo-heading"
      >
        <div className="max-w-4xl mx-auto space-y-8 text-white/70 leading-relaxed text-[15px]">
          <div>
            <h2 id="fs-seo-heading" className="text-2xl md:text-3xl font-bold text-white mb-4">
              Service Fichiers ECU en ligne — Auvergne Tech Racing
            </h2>
            <p>
              Vous êtes <strong>préparateur, garagiste ou concessionnaire</strong> et cherchez un partenaire fiable
              pour la reprogrammation moteur de vos clients ? Notre <strong>service fichiers ECU en ligne</strong> vous permet
              d&apos;envoyer votre fichier moteur (Alientech KESSv3, KTAG, Magic Motorsport, CMD Flash, MPPS…) et de
              recevoir une <strong>cartographie optimisée sous 24 h ouvrées</strong>. Nos ingénieurs cartographient au quotidien
              des véhicules essence, diesel, hybrides et industriels sur banc de puissance
              (jusqu&apos;à 1000 ch), avec un tuning respectueux de la mécanique et de la durée de vie du moteur.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Nos types de cartographie</h3>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong>Stage 1</strong> — gain de puissance et de couple sans modification mécanique, économies de
                carburant, agréments de conduite (à partir de 250 €).
              </li>
              <li>
                <strong>Stage 2</strong> — optimisation complète avec modifications matérielles (downpipe, admission,
                catalyseur sport…), pour tirer le meilleur d&apos;une préparation.
              </li>
              <li>
                <strong>Flexfuel E85</strong> — cartographie éthanol compatible avec la conversion Flexfuel,
                pour rouler à l&apos;éthanol tout en gardant la souplesse essence.
              </li>
              <li>
                <strong>Suppression FAP / EGR / AdBlue</strong> — pour circuits fermés uniquement, dans le respect
                des usages autorisés.
              </li>
              <li>
                <strong>Off DTC / MAF / Speed limiter</strong> — corrections spécifiques sur demande.
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Comment ça marche ?</h3>
            <ol className="list-decimal pl-6 space-y-1.5">
              <li>Créez votre compte professionnel en quelques secondes (validation manuelle sous 2 h ouvrées).</li>
              <li>Chargez votre fichier ECU original (formats .ori, .bin, .full, .frf…) et précisez le type de tuning.</li>
              <li>Réglez la prestation en ligne par carte bancaire (paiement sécurisé Stripe).</li>
              <li>Recevez votre fichier optimisé sous 24 h ouvrées avec support technique dédié.</li>
              <li>Flashez sereinement grâce à notre garantie satisfait ou re-cartographié.</li>
            </ol>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Pourquoi choisir Auvergne Tech Racing ?</h3>
            <p>
              Basés à <strong>Clermont-Ferrand (Auvergne)</strong>, nous cumulons plus de 8 ans d&apos;expérience sur des
              véhicules essence, diesel et hybrides — <strong>Peugeot, Renault, Citroën, DS, Ford, Volkswagen, Audi,
              BMW, Mercedes, Volvo, Toyota, Nissan, Hyundai, Kia</strong> — sans oublier les utilitaires,
              camions et engins agricoles. Chaque fichier est testé sur banc avant livraison et nos cartographies
              conservent les seuils de sécurité constructeur (pression de rail, richesse, température de gaz d&apos;échappement)
              pour préserver la fiabilité de votre moteur.
            </p>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="border-t border-white/[0.04] py-8 px-6 text-center text-white/30 text-xs">
        © {new Date().getFullYear()} Auvergne Tech Racing — 47 rue Jules Verne, 63100 Clermont-Ferrand
      </footer>
    </div>
  );
};

// ============== AUTH SCREENS ==============
const AuthScreen = ({ onLogin, resetToken, onResetDone, initialMode, onBack }) => {
  const [mode, setMode] = useState(resetToken ? "reset" : (initialMode || "login"));
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", confirm_password: "",
    client_type: "particulier",
    first_name: "", last_name: "",
    company_name: "",
    address_street: "", address_postal_code: "", address_city: "", address_country: "France",
    phone: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // FORGOT PASSWORD — send reset email
      if (mode === "forgot") {
        const fd = new FormData();
        fd.append("email", form.email);
        await axios.post(`${API}/auth/forgot-password`, fd);
        toast.success("Si un compte existe, un email vient d'être envoyé.");
        setMode("login");
        return;
      }

      // RESET PASSWORD — complete reset with token from URL
      if (mode === "reset") {
        if (form.password !== form.confirm_password) {
          toast.error("Les mots de passe ne correspondent pas");
          return;
        }
        if (form.password.length < 6) {
          toast.error("Le mot de passe doit contenir au moins 6 caractères");
          return;
        }
        const fd = new FormData();
        fd.append("token", resetToken);
        fd.append("new_password", form.password);
        await axios.post(`${API}/auth/reset-password`, fd);
        toast.success("Mot de passe réinitialisé ! Vous pouvez maintenant vous connecter.");
        onResetDone?.();
        setMode("login");
        setForm({ ...form, password: "", confirm_password: "" });
        return;
      }

      // LOGIN / REGISTER
      const formData = new FormData();
      formData.append("email", form.email);
      formData.append("password", form.password);
      if (mode === "register") {
        formData.append("client_type", form.client_type);
        formData.append("first_name", form.first_name);
        formData.append("last_name", form.last_name);
        formData.append("company_name", form.client_type === "professionnel" ? form.company_name : "");
        formData.append("address_street", form.address_street);
        formData.append("address_postal_code", form.address_postal_code);
        formData.append("address_city", form.address_city);
        formData.append("address_country", form.address_country);
        formData.append("phone", form.phone);
      }
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const res = await axios.post(`${API}${endpoint}`, formData);
      localStorage.setItem("fs_token", res.data.token);
      localStorage.setItem("fs_dealer", JSON.stringify(res.data.dealer));
      onLogin(res.data.dealer, res.data.token);
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-10">
      <div className={`w-full ${mode === "register" ? "max-w-2xl" : "max-w-md"}`}>
        {onBack && !resetToken && (
          <button
            type="button"
            onClick={onBack}
            data-testid="fs-auth-back-landing"
            className="text-white/40 hover:text-white/70 text-xs mb-4 flex items-center gap-1 transition-colors"
          >
            <ChevronLeft size={14} /> Retour à l'accueil
          </button>
        )}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-black tracking-tighter uppercase italic text-white">
            AUVERGNE<span className="text-[#FF3B30]"> TECH RACING</span>
          </h1>
          <p className="text-white/40 mt-2 text-sm tracking-widest uppercase">Service de fichiers</p>
        </div>

        <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-8">
          {(mode === "forgot" || mode === "reset") ? (
            <div className="mb-6 text-center">
              <h2 className="text-lg font-bold text-white" data-testid="fs-auth-mode-title">
                {mode === "forgot" ? "Mot de passe oublié" : "Nouveau mot de passe"}
              </h2>
              <p className="text-white/40 text-xs mt-1">
                {mode === "forgot"
                  ? "Saisissez votre email pour recevoir un lien"
                  : "Choisissez un nouveau mot de passe"}
              </p>
            </div>
          ) : (
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setMode("login")}
                data-testid="fs-auth-login-tab"
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === "login" ? "bg-[#FF3B30] text-white" : "text-white/40 hover:text-white/60"
                }`}
              >Connexion</button>
              <button
                onClick={() => setMode("register")}
                data-testid="fs-auth-register-tab"
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  mode === "register" ? "bg-[#FF3B30] text-white" : "text-white/40 hover:text-white/60"
                }`}
              >Inscription</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                {/* Type de client */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                    Type de client <span className="text-[#FF3B30]">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: "particulier", label: "Particulier" },
                      { value: "professionnel", label: "Professionnel" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`fs-register-type-${opt.value}`}
                        onClick={() => setForm({ ...form, client_type: opt.value })}
                        className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                          form.client_type === opt.value
                            ? "bg-[#FF3B30] text-white border-transparent"
                            : "bg-[#1C1C1E] text-white/60 border-white/[0.06] hover:border-white/[0.15]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Identité */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                      Prénom <span className="text-[#FF3B30]">*</span>
                    </label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                      <input
                        data-testid="fs-register-firstname"
                        type="text" required value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                        placeholder="Jean"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                      Nom <span className="text-[#FF3B30]">*</span>
                    </label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                      <input
                        data-testid="fs-register-lastname"
                        type="text" required value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                        placeholder="Dupont"
                      />
                    </div>
                  </div>
                </div>

                {/* Entreprise (si pro) */}
                {form.client_type === "professionnel" && (
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                      Nom de l'entreprise <span className="text-[#FF3B30]">*</span>
                    </label>
                    <div className="relative">
                      <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                      <input
                        data-testid="fs-register-company"
                        type="text" required value={form.company_name}
                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                        className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                        placeholder="Votre entreprise"
                      />
                    </div>
                  </div>
                )}

                {/* Adresse */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                    Adresse postale <span className="text-[#FF3B30]">*</span>
                  </label>
                  <input
                    data-testid="fs-register-street"
                    type="text" required value={form.address_street}
                    onChange={(e) => setForm({ ...form, address_street: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                    placeholder="15 rue de la Paix"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                      Code postal <span className="text-[#FF3B30]">*</span>
                    </label>
                    <input
                      data-testid="fs-register-postal"
                      type="text" required value={form.address_postal_code}
                      onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                      placeholder="63000"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                      Ville <span className="text-[#FF3B30]">*</span>
                    </label>
                    <input
                      data-testid="fs-register-city"
                      type="text" required value={form.address_city}
                      onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                      placeholder="Clermont-Ferrand"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Pays</label>
                  <input
                    data-testid="fs-register-country"
                    type="text" value={form.address_country}
                    onChange={(e) => setForm({ ...form, address_country: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                    placeholder="France"
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                    Téléphone <span className="text-[#FF3B30]">*</span>
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                    <input
                      data-testid="fs-register-phone"
                      type="tel" required value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>
                </div>
              </>
            )}
            {/* Email field — hidden in reset mode (only password + confirm) */}
            {mode !== "reset" && (
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
                  <input
                    data-testid="fs-auth-email"
                    type="email" required value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                    placeholder="email@exemple.com"
                  />
                </div>
              </div>
            )}

            {/* Password field — hidden in forgot mode (email only) */}
            {mode !== "forgot" && (
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">
                  {mode === "reset" ? "Nouveau mot de passe" : "Mot de passe"}
                </label>
                <input
                  data-testid="fs-auth-password"
                  type="password" required value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                  placeholder={mode === "reset" ? "Minimum 6 caractères" : "Mot de passe"}
                />
                {mode === "login" && (
                  <button
                    data-testid="fs-auth-forgot-link"
                    type="button"
                    onClick={() => setMode("forgot")}
                    className="mt-2 text-[#FF3B30] hover:text-[#ff6b62] text-xs"
                  >Mot de passe oublié ?</button>
                )}
              </div>
            )}

            {/* Confirm password field — reset mode only */}
            {mode === "reset" && (
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Confirmer le mot de passe</label>
                <input
                  data-testid="fs-auth-confirm-password"
                  type="password" required value={form.confirm_password}
                  onChange={e => setForm({...form, confirm_password: e.target.value})}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                  placeholder="Retapez le même mot de passe"
                />
              </div>
            )}

            <button
              data-testid="fs-auth-submit"
              type="submit" disabled={loading}
              className="w-full bg-[#FF3B30] hover:bg-[#D63025] text-white py-3 rounded-lg font-medium transition-all disabled:opacity-50 mt-2"
            >
              {loading ? "Chargement..." :
                mode === "login" ? "Se connecter" :
                mode === "register" ? "Créer un compte" :
                mode === "forgot" ? "Envoyer le lien" :
                "Réinitialiser le mot de passe"}
            </button>

            {(mode === "forgot" || mode === "reset") && (
              <div className="text-center pt-1">
                <button
                  data-testid="fs-auth-back-to-login"
                  type="button"
                  onClick={() => {
                    setMode("login");
                    if (mode === "reset") onResetDone?.();
                  }}
                  className="text-white/40 hover:text-white/70 text-xs"
                >← Retour à la connexion</button>
              </div>
            )}
          </form>
        </div>

        <div className="text-center mt-6">
          <a href="/" className="text-white/30 hover:text-white/50 text-sm transition-colors">Retour au site</a>
        </div>
      </div>
    </div>
  );
};

// ============== SIDEBAR ==============
const Sidebar = ({ activePage, onNavigate, dealer, onLogout }) => {
  const navItems = [
    { id: "dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
    { id: "upload", icon: Upload, label: "Télécharger" },
    { id: "files", icon: FileText, label: "Fichiers" },
    { id: "profile", icon: UserCog, label: "Mon profil" },
  ];

  return (
    <div className="w-[72px] lg:w-[200px] bg-[#0A0A0A] border-r border-white/[0.04] flex flex-col min-h-screen shrink-0">
      <div className="p-4 lg:p-5 border-b border-white/[0.04]">
        <div className="hidden lg:block">
          <h2 className="text-xs font-black tracking-tighter uppercase text-white">
            ATR<span className="text-[#FF3B30]"> FILES</span>
          </h2>
        </div>
        <div className="lg:hidden flex justify-center">
          <span className="text-white font-black text-sm">ATR</span>
        </div>
      </div>

      <nav className="flex-1 py-4 space-y-1 px-2">
        {navItems.map(item => (
          <button
            key={item.id}
            data-testid={`fs-nav-${item.id}`}
            onClick={() => onNavigate(item.id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
              activePage === item.id
                ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                : "text-white/40 hover:text-white/70 hover:bg-white/[0.03]"
            }`}
          >
            <item.icon size={18} />
            <span className="hidden lg:inline">{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="p-3 border-t border-white/[0.04]">
        <div className="hidden lg:block px-2 mb-3">
          <p className="text-white/60 text-xs truncate">
            {dealer?.company_name || (`${dealer?.first_name || ""} ${dealer?.last_name || ""}`.trim()) || dealer?.email}
          </p>
          <p className="text-white/30 text-xs truncate">{dealer?.email}</p>
        </div>
        <button
          data-testid="fs-logout"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/5 transition-all text-sm"
        >
          <LogOut size={18} />
          <span className="hidden lg:inline">Déconnexion</span>
        </button>
      </div>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = ({ token, onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [recentFiles, setRecentFiles] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [filesRes] = await Promise.all([
          axios.get(`${API}/requests?token=${token}`)
        ]);
        setRecentFiles(filesRes.data.slice(0, 5));
        const files = filesRes.data;
        setStats({
          total: files.length,
          pending: files.filter(f => f.status === "pending").length,
          in_progress: files.filter(f => f.status === "in_progress").length,
          completed: files.filter(f => f.status === "completed").length,
        });
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [token]);

  const statCards = stats ? [
    { label: "Total fichiers", value: stats.total, color: "text-white" },
    { label: "En attente", value: stats.pending, color: "text-yellow-400" },
    { label: "En cours", value: stats.in_progress, color: "text-blue-400" },
    { label: "Terminés", value: stats.completed, color: "text-green-400" },
  ] : [];

  return (
    <div data-testid="fs-dashboard" className="space-y-6">
      <h1 className="text-xl font-bold text-white">Tableau de bord</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div key={i} className="bg-[#121212] border border-white/[0.06] rounded-xl p-5">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#121212] border border-white/[0.06] rounded-xl">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.04]">
          <h2 className="text-white font-medium text-sm">Derniers fichiers</h2>
          <button onClick={() => onNavigate("files")} className="text-[#FF3B30] text-xs hover:underline">
            Voir tout
          </button>
        </div>
        {recentFiles.length === 0 ? (
          <div className="p-8 text-center text-white/30 text-sm">
            Aucun fichier. Commencez par télécharger un fichier.
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {recentFiles.map(file => {
              const st = STATUS_MAP[file.status] || STATUS_MAP.pending;
              return (
                <div key={file.id} className="px-5 py-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {file.vehicle_brand} {file.vehicle_model} {file.vehicle_year}
                    </p>
                    <p className="text-white/30 text-xs">{file.vin || "—"}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== UPLOAD FORM ==============
const UploadPage = ({ token, onNavigate }) => {
  const [step, setStep] = useState(1);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    vehicle_type: "", vehicle_brand: "", vehicle_model: "",
    vehicle_year: "", vehicle_engine: "",
    file: null, file_type: "ecu", vin: "", ecu_type: "",
    transmission: "manuelle", tool: "",
    service_type_id: "", service_options: [],
    is_professional: true, client_type: "professionnel", notes: ""
  });

  useEffect(() => {
    const fetchTypes = () => {
      axios.get(`${API}/service-types`).then(r => setServiceTypes(r.data)).catch(() => {});
    };
    fetchTypes();
    // Refetch every time user switches to step 2 (where services are chosen)
  }, []);

  // Re-fetch service types when user reaches step 2 (ensures latest prices)
  useEffect(() => {
    if (step !== 2) return;
    const refetch = () => {
      axios.get(`${API}/service-types`).then(r => setServiceTypes(r.data)).catch(() => {});
    };
    refetch();
    // Poll every 20s while on step 2 to catch admin price changes in real-time
    const interval = setInterval(refetch, 20000);
    return () => clearInterval(interval);
  }, [step]);

  // Derived: service lookup (for defaults/live refresh only)
  const selectedService = useMemo(
    () => serviceTypes.find(s => s.id === form.service_type_id) || null,
    [serviceTypes, form.service_type_id]
  );
  // Primary source of truth: price/name stored at CLICK time.
  // These are 100% reactive to the user's click and immune to duplicate/stale IDs.
  const selectedPrice = form.service_type_id
    ? (form.service_base_price ?? selectedService?.base_price ?? 0)
    : 0;
  const selectedName = form.service_type_name || selectedService?.name || "";

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setForm({...form, file: e.dataTransfer.files[0]});
  };

  const handleSubmit = async () => {
    if (!form.file) { toast.error("Veuillez sélectionner un fichier"); return; }
    if (form.file.size > 50 * 1024 * 1024) { toast.error("Fichier trop lourd (max 50 Mo)"); return; }
    if (!form.vehicle_brand) { toast.error("Veuillez renseigner la marque du véhicule"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", form.file);
      Object.keys(form).forEach(key => {
        if (key !== "file" && key !== "service_options") {
          fd.append(key, form[key]);
        }
      });
      fd.append("service_options", JSON.stringify(form.service_options));
      const res = await axios.post(`${API}/requests`, fd);
      toast.success("Fichier envoyé avec succès !");
      // Navigate directly to the newly created request's detail page
      if (res.data && res.data.id) {
        onNavigate("files", res.data.id);
      } else {
        onNavigate("files");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const tools = [
    "Alientech KESSv2", "Alientech K-TAG", "Autotuner", "CMD Flash",
    "Dimsport", "bFlash", "MPPS", "PCM Flash", "MagicMotorSport", "Autre"
  ];

  return (
    <div data-testid="fs-upload-page" className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-white">Télécharger un fichier</h1>

      {/* Progress steps */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setStep(1)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            step === 1 ? "bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20" : "text-white/40"
          }`}
        >
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            step === 1 ? "bg-[#FF3B30] text-white" : "bg-white/10 text-white/40"
          }`}>1</span>
          Véhicule & Fichier
        </button>
        <ChevronRight size={16} className="text-white/20" />
        <button
          onClick={() => step >= 1 && setStep(2)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            step === 2 ? "bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20" : "text-white/40"
          }`}
        >
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            step === 2 ? "bg-[#FF3B30] text-white" : "bg-white/10 text-white/40"
          }`}>2</span>
          Service & Options
        </button>
      </div>

      {step === 1 && (
        <div className="space-y-6">
          {/* Vehicle Selection */}
          <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                <Settings size={16} className="text-[#FF3B30]" />
              </div>
              <h2 className="text-white font-medium">Sélectionnez le véhicule</h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { key: "vehicle_type", label: "Type", placeholder: "Voiture, Camion..." },
                { key: "vehicle_brand", label: "Constructeur", placeholder: "BMW, Audi..." },
                { key: "vehicle_model", label: "Modèle", placeholder: "Série 3, A4..." },
                { key: "vehicle_year", label: "Année", placeholder: "2020" },
                { key: "vehicle_engine", label: "Motorisation", placeholder: "2.0 TDI..." },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-white/40 text-xs mb-1 block">{f.label}</label>
                  <input
                    data-testid={`fs-upload-${f.key}`}
                    type="text" value={form[f.key]}
                    onChange={e => setForm({...form, [f.key]: e.target.value})}
                    placeholder={f.placeholder}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none placeholder:text-white/20"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* File Upload */}
            <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                  <Upload size={16} className="text-[#FF3B30]" />
                </div>
                <h2 className="text-white font-medium">Téléverser un fichier</h2>
              </div>
              <div
                data-testid="fs-upload-dropzone"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  dragActive ? "border-[#FF3B30] bg-[#FF3B30]/5" :
                  form.file ? "border-green-500/30 bg-green-500/5" :
                  "border-white/10 hover:border-white/20"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={e => e.target.files?.[0] && setForm({...form, file: e.target.files[0]})}
                />
                {form.file ? (
                  <div>
                    <CheckCircle size={32} className="mx-auto text-green-400 mb-3" />
                    <p className="text-white text-sm font-medium">{form.file.name}</p>
                    <p className="text-white/30 text-xs mt-1">{(form.file.size / 1024).toFixed(1)} KB</p>
                    <p className="text-[#FF3B30] text-xs mt-2 hover:underline">Changer de fichier</p>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto text-white/20 mb-3" />
                    <p className="text-white/50 text-sm">Glissez-déposez ou cliquez</p>
                    <p className="text-white/20 text-xs mt-1">Fichier ECU original</p>
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                  <AlertCircle size={16} className="text-[#FF3B30]" />
                </div>
                <h2 className="text-white font-medium">Détails</h2>
              </div>

              {/* File type toggle */}
              <div className="flex gap-2 mb-4">
                {[{v: "ecu", l: "Fichier ECU"}, {v: "transmission", l: "Fichier transmission"}].map(t => (
                  <button
                    key={t.v}
                    data-testid={`fs-upload-filetype-${t.v}`}
                    onClick={() => setForm({...form, file_type: t.v})}
                    className={`flex-1 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                      form.file_type === t.v ? "border-[#FF3B30]/30 bg-[#FF3B30]/10 text-[#FF3B30]" : "border-white/[0.06] text-white/40"
                    }`}
                  >{t.l}</button>
                ))}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-white/40 text-xs mb-1 block">VIN</label>
                  <input
                    data-testid="fs-upload-vin"
                    type="text" value={form.vin}
                    onChange={e => setForm({...form, vin: e.target.value.toUpperCase()})}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                    placeholder="Numéro VIN"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Type d'ECU</label>
                  <input
                    data-testid="fs-upload-ecu_type"
                    type="text" value={form.ecu_type}
                    onChange={e => setForm({...form, ecu_type: e.target.value})}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                    placeholder="Ex: Bosch EDC17C46"
                  />
                </div>

                {/* Transmission */}
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Transmission</label>
                  <div className="flex gap-2">
                    {[{v: "automatique", l: "Automatique"}, {v: "manuelle", l: "Manuelle"}].map(t => (
                      <button
                        key={t.v}
                        data-testid={`fs-upload-transmission-${t.v}`}
                        onClick={() => setForm({...form, transmission: t.v})}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                          form.transmission === t.v ? "border-[#FF3B30]/30 bg-[#FF3B30]/10 text-[#FF3B30]" : "border-white/[0.06] text-white/40"
                        }`}
                      >{t.l}</button>
                    ))}
                  </div>
                </div>

                {/* Tool */}
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Outil de lecture</label>
                  <select
                    data-testid="fs-upload-tool"
                    value={form.tool}
                    onChange={e => setForm({...form, tool: e.target.value})}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                  >
                    <option value="">Sélectionner l'outil</option>
                    {tools.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Client type */}
                <div>
                  <label className="text-white/40 text-xs mb-1 block">Type de client</label>
                  <div className="flex gap-2">
                    {[{v: "professionnel", l: "Professionnel"}, {v: "particulier", l: "Particulier"}].map(t => (
                      <button
                        key={t.v}
                        data-testid={`fs-upload-clienttype-${t.v}`}
                        onClick={() => setForm({...form, client_type: t.v, is_professional: t.v === "professionnel"})}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-all ${
                          form.client_type === t.v ? "border-[#FF3B30]/30 bg-[#FF3B30]/10 text-[#FF3B30]" : "border-white/[0.06] text-white/40"
                        }`}
                      >{t.l}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              data-testid="fs-upload-next"
              onClick={() => setStep(2)}
              className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-8 py-3 rounded-lg font-medium transition-all flex items-center gap-2"
            >
              Suivant <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-[1fr_280px] gap-6">
            {/* Service List - Radio selection */}
            <div className="space-y-6">
              <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                    <Settings size={16} className="text-[#FF3B30]" />
                  </div>
                  <h2 className="text-white font-medium">Solution</h2>
                </div>

                <div className="space-y-1">
                  {serviceTypes.map(st => {
                    const isSelected = form.service_type_id === st.id;
                    return (
                      <button
                        key={st.id}
                        type="button"
                        data-testid={`fs-upload-service-${st.id}`}
                        onClick={() => setForm(prev => ({
                          ...prev,
                          service_type_id: st.id,
                          service_type_name: st.name,
                          service_base_price: st.base_price || 0,
                        }))}
                        className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                          isSelected
                            ? "border-[#FF3B30]/30 bg-[#FF3B30]/5"
                            : "border-transparent hover:bg-white/[0.02]"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Radio */}
                          <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            isSelected ? "border-[#FF3B30]" : "border-white/20"
                          }`}>
                            {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]" />}
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-4">
                              <h3 className={`text-sm font-bold ${isSelected ? "text-white" : "text-white/80"}`}>
                                {st.name}
                              </h3>
                              <span className={`text-sm font-bold shrink-0 ${isSelected ? "text-[#FF3B30]" : "text-white/60"}`}>
                                {st.base_price > 0 ? `${st.base_price.toFixed(2)} €` : "0,00 €"}
                              </span>
                            </div>
                            {st.description && (
                              <p className="text-white/35 text-xs mt-1.5 leading-relaxed">{st.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Commentaires */}
              <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center">
                    <MessageSquare size={16} className="text-[#FF3B30]" />
                  </div>
                  <h2 className="text-white font-medium">Commentaires</h2>
                </div>
                <textarea
                  data-testid="fs-upload-notes"
                  value={form.notes}
                  onChange={e => setForm({...form, notes: e.target.value})}
                  rows={4}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none resize-none"
                  placeholder="Si vous avez des commentaires ou des demandes spéciales, veuillez les ajouter ici."
                />
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="text-white/40 hover:text-white px-6 py-3 rounded-lg transition-all flex items-center gap-2 border border-white/[0.06]"
                >
                  <ChevronLeft size={16} /> Précédent
                </button>
                <button
                  data-testid="fs-upload-submit"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-8 py-3 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Envoi...</> : <><CheckCircle size={16} /> Soumettre</>}
                </button>
              </div>
            </div>

            {/* Sidebar - Prix */}
            <div className="lg:sticky lg:top-6 h-fit">
              <div className="bg-[#121212] border border-white/[0.06] rounded-xl overflow-hidden">
                <div className="p-5 border-b border-white/[0.04]">
                  <h3 className="text-white font-medium text-sm flex items-center gap-2">
                    <FileText size={14} /> Votre fichier
                  </h3>
                </div>
                <div className="p-5">
                  {/* Price Button */}
                  <div className={`w-full py-3 px-4 rounded-lg text-center font-bold text-sm transition-all ${
                    form.service_type_id
                      ? "bg-[#FF3B30] text-white"
                      : "bg-[#FF3B30]/20 text-[#FF3B30]"
                  }`}>
                    {form.service_type_id ? (
                      <>Prix : {selectedPrice.toFixed(2)} €</>
                    ) : (
                      <>Prix</>
                    )}
                  </div>

                  {!form.service_type_id ? (
                    <p className="text-white/30 text-xs text-center mt-4">
                      Veuillez sélectionner votre étape.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-2 text-xs">
                      <div className="flex justify-between text-white/40">
                        <span>Service</span>
                        <span className="text-white font-medium">{selectedName}</span>
                      </div>
                      <div className="flex justify-between text-white/40">
                        <span>Véhicule</span>
                        <span className="text-white">{form.vehicle_brand} {form.vehicle_model}</span>
                      </div>
                      <div className="flex justify-between text-white/40">
                        <span>Fichier</span>
                        <span className="text-white truncate ml-2">{form.file?.name || "—"}</span>
                      </div>
                      <div className="border-t border-white/[0.04] pt-2 mt-3 flex justify-between text-white">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-[#FF3B30]">
                          {selectedPrice.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BENSKY Vehicle Widget */}
      <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
        <h2 className="text-white font-medium mb-4">Données du véhicule</h2>
        <iframe
          src="https://portal.bensky.co.uk/w.php?id=1739&theme=dark&color=FF3B30&differencecolor=474747"
          style={{ border: "0px", transition: "height 0.5s" }}
          width="100%"
          height="350"
          allowtransparency="true"
          title="Vehicle search"
        />
      </div>
    </div>
  );
};

// ============== PROFILE PAGE ==============
const ProfilePage = ({ token, dealer, onUpdated }) => {
  const [form, setForm] = useState({
    client_type: dealer?.client_type || "particulier",
    first_name: dealer?.first_name || "",
    last_name: dealer?.last_name || "",
    company_name: dealer?.company_name || "",
    address_street: dealer?.address_street || "",
    address_postal_code: dealer?.address_postal_code || "",
    address_city: dealer?.address_city || "",
    address_country: dealer?.address_country || "France",
    phone: dealer?.phone || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.client_type === "professionnel" && !form.company_name.trim()) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await axios.put(`${API}/auth/profile`, fd);
      const updated = res.data.dealer;
      localStorage.setItem("fs_dealer", JSON.stringify(updated));
      if (onUpdated) onUpdated(updated);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <UserCog size={22} className="text-[#FF3B30]" />
          Mon profil
        </h1>
        <p className="text-white/50 text-sm mt-1">
          Mettez à jour vos informations personnelles et professionnelles.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-[#141414] border border-white/[0.06] rounded-xl p-6 space-y-5">
        {/* Email (readonly) */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Email (non modifiable)</label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
            <input
              type="email" value={dealer?.email || ""} readOnly
              className="w-full bg-[#1C1C1E]/50 border border-white/[0.04] rounded-lg pl-10 pr-4 py-3 text-white/60 text-sm cursor-not-allowed"
            />
          </div>
          <p className="text-[11px] text-white/30 mt-1.5">Pour changer d'email, contactez le garage.</p>
        </div>

        {/* Type */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Type de client</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: "particulier", label: "Particulier" },
              { value: "professionnel", label: "Professionnel" },
            ].map((opt) => (
              <button
                key={opt.value} type="button"
                onClick={() => setForm({ ...form, client_type: opt.value })}
                className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                  form.client_type === opt.value
                    ? "bg-[#FF3B30] text-white border-transparent"
                    : "bg-[#1C1C1E] text-white/60 border-white/[0.06] hover:border-white/[0.15]"
                }`}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {/* Identité */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Prénom *</label>
            <input
              required type="text" value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nom *</label>
            <input
              required type="text" value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
        </div>

        {/* Entreprise */}
        {form.client_type === "professionnel" && (
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nom de l'entreprise *</label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
              <input
                required type="text" value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
              />
            </div>
          </div>
        )}

        {/* Adresse */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Adresse postale *</label>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
            <input
              required type="text" value={form.address_street}
              onChange={(e) => setForm({ ...form, address_street: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Code postal *</label>
            <input
              required type="text" value={form.address_postal_code}
              onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Ville *</label>
            <input
              required type="text" value={form.address_city}
              onChange={(e) => setForm({ ...form, address_city: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Pays</label>
          <input
            type="text" value={form.address_country}
            onChange={(e) => setForm({ ...form, address_country: e.target.value })}
            className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
          />
        </div>

        {/* Téléphone */}
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Téléphone *</label>
          <div className="relative">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
            <input
              required type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-10 pr-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
            />
          </div>
        </div>

        <button
          type="submit" disabled={saving}
          data-testid="fs-profile-save-btn"
          className="w-full bg-[#FF3B30] hover:bg-[#D63025] disabled:bg-[#FF3B30]/50 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <><Loader2 size={16} className="animate-spin" /> Enregistrement...</> : <><Save size={15} /> Enregistrer les modifications</>}
        </button>
      </form>

      {/* ────── CHANGE PASSWORD ────── */}
      <ChangePasswordForm token={token} />
    </div>
  );
};

const ChangePasswordForm = ({ token }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("current_password", form.current_password);
      fd.append("new_password", form.new_password);
      await axios.put(`${API}/auth/password`, fd);
      toast.success("Mot de passe mis à jour");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white font-semibold flex items-center gap-2">
            <ShieldCheck size={17} className="text-amber-400" /> Mot de passe
          </h2>
          <p className="text-white/50 text-xs mt-0.5">Mettez à jour régulièrement votre mot de passe.</p>
        </div>
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="text-xs text-white/60 hover:text-white transition-colors"
        >
          {show ? "Annuler" : "Modifier"}
        </button>
      </div>

      {show && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Mot de passe actuel *</label>
            <input
              required type="password" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
              data-testid="fs-profile-current-password"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nouveau mot de passe *</label>
              <input
                required type="password" value={form.new_password} minLength={6}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                data-testid="fs-profile-new-password"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Confirmer *</label>
              <input
                required type="password" value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-3 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
                data-testid="fs-profile-confirm-password"
              />
            </div>
          </div>
          <button
            type="submit" disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
            data-testid="fs-profile-password-submit"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement...</> : <><ShieldCheck size={14} /> Changer le mot de passe</>}
          </button>
        </form>
      )}
    </div>
  );
};


// ============== FILE LIST ==============
const FilesPage = ({ token, onSelectFile }) => {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/requests?token=${token}&search=${search}`);
      setFiles(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  return (
    <div data-testid="fs-files-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Fichiers</h1>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
          <input
            data-testid="fs-files-search"
            type="text" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-9 pr-4 py-2 text-white text-sm w-64 focus:border-[#FF3B30]/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="bg-[#121212] border border-white/[0.06] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04]">
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">#</th>
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">Dernière MAJ</th>
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">Véhicule</th>
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">VIN</th>
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">Service</th>
              <th className="text-left text-white/40 text-xs uppercase tracking-wider px-5 py-3">Statut</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-white/30 text-sm">Chargement...</td></tr>
            ) : files.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-white/30 text-sm">Aucun fichier disponible</td></tr>
            ) : files.map((file, i) => {
              const st = STATUS_MAP[file.status] || STATUS_MAP.pending;
              return (
                <tr
                  key={file.id}
                  data-testid={`fs-file-row-${i}`}
                  className="border-b border-white/[0.02] hover:bg-white/[0.02] cursor-pointer transition-colors"
                  onClick={() => onSelectFile(file.id)}
                >
                  <td className="px-5 py-3 text-white/40 text-sm">{files.length - i}</td>
                  <td className="px-5 py-3 text-white/60 text-sm">
                    {new Date(file.updated_at).toLocaleDateString("fr-FR", {day: "2-digit", month: "2-digit", year: "numeric"})}
                  </td>
                  <td className="px-5 py-3 text-white text-sm font-medium">
                    {file.vehicle_brand} {file.vehicle_model} {file.vehicle_year}
                  </td>
                  <td className="px-5 py-3 text-white/50 text-sm font-mono">{file.vin || "—"}</td>
                  <td className="px-5 py-3 text-[#FF3B30] text-sm">{file.service_type_name || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                  </td>
                  <td className="px-5 py-3">
                    <Eye size={14} className="text-white/20" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-white/[0.04] text-white/30 text-xs">
          {files.length} fichier(s)
        </div>
      </div>
    </div>
  );
};

// ============== FILE DETAIL ==============
const FileDetail = ({ token, fileId, onBack }) => {
  const [file, setFile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [fRes, mRes] = await Promise.all([
          axios.get(`${API}/requests/${fileId}?token=${token}`),
          axios.get(`${API}/requests/${fileId}/messages?token=${token}`)
        ]);
        setFile(fRes.data);
        setMessages(mRes.data);
      } catch (err) {
        toast.error("Erreur chargement fichier");
      } finally {
        setLoading(false);
      }
    };
    load();

    // ────── Real-time SSE ──────
    let es = null;
    let fallbackInterval = null;
    const refetch = async () => {
      try {
        const [fRes, mRes] = await Promise.all([
          axios.get(`${API}/requests/${fileId}?token=${token}`),
          axios.get(`${API}/requests/${fileId}/messages?token=${token}`)
        ]);
        setFile(fRes.data);
        setMessages(mRes.data);
      } catch {}
    };

    try {
      es = new EventSource(`${API}/requests/${fileId}/events?token=${token}`);
      es.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "message") {
            setMessages((prev) => {
              if (prev.some((m) => m.id === evt.data.id)) return prev;
              return [...prev, evt.data];
            });
          } else if (evt.type === "price_changed" || evt.type === "status_changed") {
            refetch();
          }
        } catch {}
      };
      es.onerror = () => {
        // SSE failed → start fallback polling
        if (!fallbackInterval) {
          fallbackInterval = setInterval(refetch, 15000);
        }
      };
    } catch {
      // EventSource unavailable → polling fallback
      fallbackInterval = setInterval(refetch, 15000);
    }

    return () => {
      if (es) es.close();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [fileId, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("message", newMsg);
      const res = await axios.post(`${API}/requests/${fileId}/messages`, fd);
      setMessages([...messages, res.data]);
      setNewMsg("");
    } catch (err) {
      toast.error("Erreur envoi message");
    } finally {
      setSending(false);
    }
  };

  const handleDownload = async () => {
    // Check if payment is required
    if (file.price > 0 && !file.is_paid) {
      toast.error("Paiement requis avant téléchargement");
      return;
    }
    try {
      const res = await axios.get(`${API}/requests/${fileId}/download?token=${token}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file?.modified_filename || "modified_file";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error("Paiement requis avant téléchargement");
      } else {
        toast.error(err.response?.data?.detail || "Erreur téléchargement");
      }
    }
  };

  const handlePayment = async () => {
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("origin_url", window.location.origin);
      const res = await axios.post(`${API}/requests/${fileId}/checkout`, fd);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du paiement");
    }
  };

  if (loading) return <div className="text-white/30 text-center py-12">Chargement...</div>;
  if (!file) return <div className="text-white/30 text-center py-12">Fichier non trouvé</div>;

  const st = STATUS_MAP[file.status] || STATUS_MAP.pending;

  return (
    <div data-testid="fs-file-detail" className="space-y-6">
      <button onClick={onBack} className="text-white/40 hover:text-white text-sm flex items-center gap-1 transition-colors">
        <ChevronLeft size={14} /> Retour aux fichiers
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* File Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#121212] border border-white/[0.06] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold">
                {file.vehicle_brand} {file.vehicle_model} {file.vehicle_year}
              </h2>
              <span className={`text-xs px-3 py-1.5 rounded-full ${st.color}`}>{st.label}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {[
                ["Motorisation", file.vehicle_engine],
                ["VIN", file.vin],
                ["Type d'ECU", file.ecu_type],
                ["Transmission", file.transmission],
                ["Outil", file.tool],
                ["Type fichier", file.file_type === "ecu" ? "ECU" : "Transmission"],
                ["Service", file.service_type_name],
                ["Fichier original", file.original_filename],
              ].map(([label, val]) => (
                <div key={label}>
                  <span className="text-white/40">{label}:</span>
                  <span className="text-white ml-2">{val || "—"}</span>
                </div>
              ))}
            </div>

            {file.notes && (
              <div className="mt-4 p-3 bg-white/[0.02] rounded-lg">
                <p className="text-white/40 text-xs mb-1">Notes:</p>
                <p className="text-white/70 text-sm">{file.notes}</p>
              </div>
            )}
          </div>

          {/* Download / Payment Section */}
          {file.status === "completed" && file.modified_file_path && (
            <>
              {file.price > 0 && !file.is_paid ? (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-amber-400 font-medium flex items-center gap-2">
                        <CreditCard size={18} /> Paiement requis
                      </h3>
                      <p className="text-amber-400/50 text-sm mt-1">
                        {file.modified_filename} — <span className="font-bold text-amber-300">{file.price.toFixed(2)} EUR</span>
                      </p>
                    </div>
                    <button
                      data-testid="fs-pay-file"
                      onClick={handlePayment}
                      className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2.5 rounded-lg font-bold transition-all flex items-center gap-2"
                    >
                      <CreditCard size={16} /> Payer {file.price.toFixed(2)} EUR
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-green-400 font-medium flex items-center gap-2">
                        {file.is_paid && <ShieldCheck size={18} />}
                        Fichier modifié disponible
                        {file.is_paid && <span className="text-xs bg-green-500/20 px-2 py-0.5 rounded-full ml-2">Payé</span>}
                      </h3>
                      <p className="text-green-400/50 text-sm mt-1">{file.modified_filename}</p>
                    </div>
                    <button
                      data-testid="fs-download-modified"
                      onClick={handleDownload}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2"
                    >
                      <Download size={16} /> Télécharger
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Chat */}
        <div className="bg-[#121212] border border-white/[0.06] rounded-xl flex flex-col h-[500px]">
          <div className="px-5 py-4 border-b border-white/[0.04]">
            <h3 className="text-white font-medium text-sm flex items-center gap-2">
              <MessageSquare size={14} /> Messages
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <div className="text-white/20 text-xs text-center mt-8">
                Aucun message. Envoyez un message pour démarrer la conversation.
              </div>
            ) : messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_type === "dealer" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] rounded-xl px-4 py-2.5 ${
                  msg.sender_type === "dealer"
                    ? "bg-[#FF3B30] text-white"
                    : "bg-[#1C1C1E] text-white/80"
                }`}>
                  <p className="text-xs opacity-60 mb-1">{msg.sender_name}</p>
                  <p className="text-sm">{msg.message}</p>
                  <p className="text-[10px] opacity-40 mt-1">
                    {new Date(msg.created_at).toLocaleTimeString("fr-FR", {hour: "2-digit", minute: "2-digit"})}
                  </p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <div className="p-3 border-t border-white/[0.04]">
            <div className="flex gap-2">
              <input
                data-testid="fs-chat-input"
                type="text"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendMessage()}
                placeholder="Votre message..."
                className="flex-1 bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-3 py-2 text-white text-sm focus:border-[#FF3B30]/50 focus:outline-none"
              />
              <button
                data-testid="fs-chat-send"
                onClick={sendMessage}
                disabled={sending}
                className="bg-[#FF3B30] hover:bg-[#D63025] text-white p-2 rounded-lg transition-all disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN PORTAL ==============
const FileService = () => {
  // Fetch just the CMS SEO block for this page so admins can rename the tab/H1 without code.
  const [fsSeo, setFsSeo] = useState({});
  useEffect(() => {
    const BASE = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BASE}/api/content/fs_landing`)
      .then((r) => setFsSeo((r.data && r.data.seo) || {}))
      .catch(() => {});
  }, []);
  useSiteMeta({
    title: fsSeo.title || "Service Fichiers ECU en Ligne — Reprog 24h | ATR",
    description: fsSeo.description || "Envoyez votre fichier ECU et recevez votre optimisation moteur sous 24h. Stage 1, Stage 2, Flexfuel. Spécialiste reprogrammation à Clermont-Ferrand.",
    canonical_path: "/file-service",
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [dealer, setDealer] = useState(null);
  const [token, setToken] = useState(null);
  const [activePage, setActivePage] = useState("dashboard");
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [authInitialMode, setAuthInitialMode] = useState("login");
  const [resetToken, setResetToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("reset_token") || "";
  });

  useEffect(() => {
    // If URL carries a reset_token, clear any saved session and show reset screen
    if (resetToken) {
      localStorage.removeItem("fs_token");
      localStorage.removeItem("fs_dealer");
      setShowAuth(true);
      setLoading(false);
      return;
    }

    const savedToken = localStorage.getItem("fs_token");
    if (savedToken) {
      axios.get(`${API}/auth/me?token=${savedToken}`)
        .then(res => {
          setDealer(res.data);
          setToken(savedToken);
          setIsAuthenticated(true);

          // Handle payment callback from Stripe
          const params = new URLSearchParams(window.location.search);
          const paymentStatus = params.get("payment");
          const requestId = params.get("request_id");
          const sessionId = params.get("session_id");

          if (paymentStatus === "success" && requestId && sessionId) {
            // Verify payment and navigate to file detail
            axios.get(`${API}/requests/${requestId}/checkout-status?session_id=${sessionId}&token=${savedToken}`)
              .then(statusRes => {
                if (statusRes.data.is_paid) {
                  toast.success("Paiement confirmé ! Vous pouvez télécharger votre fichier.");
                }
                setActivePage("files");
                setSelectedFileId(requestId);
              })
              .catch(() => {});
            // Clean URL
            window.history.replaceState({}, "", window.location.pathname);
          } else if (paymentStatus === "cancelled" && requestId) {
            toast.error("Paiement annulé");
            setActivePage("files");
            setSelectedFileId(requestId);
            window.history.replaceState({}, "", window.location.pathname);
          }
        })
        .catch(() => {
          localStorage.removeItem("fs_token");
          localStorage.removeItem("fs_dealer");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (dealerData, tokenData) => {
    setDealer(dealerData);
    setToken(tokenData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout?token=${token}`);
    } catch {}
    localStorage.removeItem("fs_token");
    localStorage.removeItem("fs_dealer");
    setIsAuthenticated(false);
    setDealer(null);
    setToken(null);
  };

  const handleNavigate = (page, fileId = null) => {
    setActivePage(page);
    setSelectedFileId(fileId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={24} className="text-[#FF3B30] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // If user hasn't clicked the auth buttons yet, show the public landing page
    if (!showAuth) {
      return (
        <>
          <Toaster position="top-right" richColors />
          <FileServiceLanding
            onOpenAuth={(mode) => {
              setAuthInitialMode(mode);
              setShowAuth(true);
            }}
          />
        </>
      );
    }
    return (
      <>
        <Toaster position="top-right" richColors />
        <AuthScreen
          onLogin={handleLogin}
          resetToken={resetToken}
          initialMode={authInitialMode}
          onBack={() => setShowAuth(false)}
          onResetDone={() => {
            setResetToken("");
            window.history.replaceState({}, "", window.location.pathname);
          }}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <Toaster position="top-right" richColors />
      <Sidebar
        activePage={activePage}
        onNavigate={handleNavigate}
        dealer={dealer}
        onLogout={handleLogout}
      />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        {activePage === "dashboard" && (
          <Dashboard token={token} onNavigate={handleNavigate} />
        )}
        {activePage === "upload" && (
          <UploadPage token={token} onNavigate={handleNavigate} />
        )}
        {activePage === "files" && !selectedFileId && (
          <FilesPage token={token} onSelectFile={setSelectedFileId} />
        )}
        {activePage === "files" && selectedFileId && (
          <FileDetail token={token} fileId={selectedFileId} onBack={() => setSelectedFileId(null)} />
        )}
        {activePage === "profile" && (
          <ProfilePage token={token} dealer={dealer} onUpdated={setDealer} />
        )}
      </main>
    </div>
  );
};

export default FileService;
